import imageCompression from 'browser-image-compression';
import EXIF from 'exif-js';

/**
 * Compresses an image file and extracts its DateTimeOriginal.
 *
 * @param {File} file - The original image file
 * @returns {Promise<{compressedFile: File, base64: string, dateTaken: string|null}>}
 */
export async function compressImage(file) {
  // 1. Extract DateTimeOriginal
  let dateTaken = null;
  try {
    await new Promise((resolve) => {
      EXIF.getData(file, function() {
        // "DateTimeOriginal" tag is 0x9003 (36867)
        // EXIF.js usually exposes it as "DateTimeOriginal"
        const dt = EXIF.getTag(this, "DateTimeOriginal");
        if (dt) {
          dateTaken = dt;
        }
        resolve();
      });
    });
  } catch (error) {
    console.warn("Failed to extract EXIF data:", error);
  }

  // 2. Compress the image
  // We strip EXIF to save space, but we kept the date separately
  const options = {
    maxSizeMB: 0.5, // Target smaller size
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    exifOrientation: true, // Handle orientation before stripping
    // preserveExif: false is default for browser-image-compression if not specified?
    // Actually documentation says it "strips exif by default" or creates new canvas.
    // Let's be explicit if possible, or reliance on canvas behavior usually strips it.
  };

  try {
    const compressedFile = await imageCompression(file, options);

    // Convert to base64 for upload
    const base64 = await imageCompression.getDataUrlFromFile(compressedFile);

    return {
      compressedFile,
      base64, // This is the "data:image/jpeg;base64,..." string
      dateTaken
    };
  } catch (error) {
    console.error("Compression failed:", error);
    // Fallback: return original if compression fails
    const reader = new FileReader();
    const base64Original = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
    });
    return {
        compressedFile: file,
        base64: base64Original,
        dateTaken
    };
  }
}
